import { TemplateRef } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { HostListener } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Output } from '@angular/core';
import { Input } from '@angular/core';
import { OnChanges } from '@angular/core';
import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectableDataCategory } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataColumnExtractor } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataCategoryFilter } from 'app/modules/main/components/data-selector/data-selector.models';
import { Localisation } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableData } from 'app/modules/main/components/data-selector/data-selector.models';
import { BehaviorSubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { tap } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-data-selector',
  templateUrl: './data-selector.component.html',
  styleUrls: ['./data-selector.component.css'],
})
export class DataSelectorComponent<D, C> implements OnChanges {

  private static readonly DEBOUNCE_DELAY_MS = 200;

  @Input() open: boolean = false;
  @Input() required: boolean = false;
  @Input() singular: boolean = false;
  @Input() virtual: boolean = true;

  @Input() localisation: Localisation;

  @Input('data') openingDataset: SelectableData<D>[];

  @Input() categoryFilter?: SelectableDataCategoryFilter<D, C>;
  @Input() columnExtractor?: SelectableDataColumnExtractor<D>;

  @Input() zeroSelectedTemplate: TemplateRef<any>;
  @Input() oneSelectedTemplate: TemplateRef<any>;
  @Input() manySelectedTemplate: TemplateRef<any>;

  @Output() selectedCount = new BehaviorSubject<number>(0);
  @Output() submitted = new EventEmitter<SelectableData<D>[]>();
  @Output() cancelled = new EventEmitter<void>();

  private datasetById: Map<string, SelectableData<D>> = new Map();

  private controls = {
    category: new FormControl<SelectableDataCategory<C>>(undefined),
    searchTerm: new FormControl(''),
  };

  private _initialized: boolean = false;
  private _loading: boolean = false;
  private _selectionFilterActive: boolean = false;

  private _viewableDataset: SelectableData<D>[] = [];

  readonly form: FormGroup = new FormGroup(this.controls);

  get initialized(): boolean {
    return this._initialized;
  }

  get loading(): boolean {
    return this._loading;
  }

  get selectionFilterActive(): boolean {
    return this._selectionFilterActive;
  }

  get viewableDataset(): SelectableData<D>[] {
    return this._viewableDataset;
  }

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
  ) {
  }

  ngOnChanges(): void {
    this.initializeOnceReady();
  }

  selectAll(): void {

    this._viewableDataset
      .forEach(data => data.selected = true);

    this.refreshView();
  }

  selectNone(): void {

    this._viewableDataset
      .forEach(data => data.selected = false);

    this.refreshView();
  }

  toggleOne(
    toggleData: SelectableData<D>
  ): void {

    if (this.singular) {

      this.getSelectedDataset()
        .filter(data => data.id != toggleData.id)
        .forEach(data => data.selected = false);
    }

    toggleData.selected = !toggleData.selected;

    this.refreshView();
  }

  toggleSelectionFilter(): void {

    const selectionFilterActive = !this._selectionFilterActive;

    this.resetFilters();

    this._selectionFilterActive = selectionFilterActive;

    this.refreshView();
  }

  @HostListener('document:keydown.escape', ['$event'])
  cancel(): void {

    if (this._initialized) {
      this.rollbackToOpeningDataset();
    }

    this.cancelled.emit();

    this.resetFilters();
    this.refreshView();
  }

  submit(): void {

    this.saveSelectedAsOpeningDataset();

    this.submitted.emit(this.getSelectedDataset());

    this.resetFilters();
    this.refreshView();
  }

  trackById(
    _: number,
    o: { id: string }
  ): string {
    return o.id;
  }

  private initializeOnceReady() {

    if (this._initialized || !this.openingDataset) {
      return;
    }

    this._initialized = true;

    this.rollbackToOpeningDataset();

    this.subscribeToSearchTermChanges();
    this.subscribeToCategoryChanges();

    this.refreshView();
  }

  private getDataset(): SelectableData<D>[] {
    return [...this.datasetById.values()];
  }

  private getSelectedDataset(): SelectableData<D>[] {

    return this.getDataset()
      .filter(data => data.selected);
  }

  private isCategoryFilterMatch(
    data: SelectableData<D>
  ): boolean {

    return !this.categoryFilter ||
      this.categoryFilter.isMatch(data);
  }

  private isSearchTermFilterMatch(
    data: SelectableData<D>
  ): boolean {

    const searchTerm = this.controls.searchTerm.value;
    const searchableTerms = `${data.label} ${data.significantTerms}`;

    return !searchTerm ||
      data.id.includes(searchTerm) ||
      searchableTerms.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());
  }

  private isSelectionFilterMatch(
    data: SelectableData<D>
  ): boolean {

    return !this._selectionFilterActive ||
      this.datasetById.get(data.id).selected;
  }

  private refreshView() {

    this._viewableDataset =
      this.getDataset()
        .filter(data => this.isCategoryFilterMatch(data))
        .filter(data => this.isSearchTermFilterMatch(data))
        .filter(data => this.isSelectionFilterMatch(data));

    this.selectedCount.next(
      this.getSelectedDataset()
        .length
    );

    this.changeDetector.detectChanges();
  }

  private resetFilters() {

    this._selectionFilterActive = false;

    this.categoryFilter?.clear();

    this.controls.category.setValue(undefined);
    this.controls.searchTerm.setValue('');
  }

  private rollbackToOpeningDataset() {

    this.datasetById = new Map(
      this.openingDataset.map(data => [data.id, Object.assign({}, data)])
    );
  }

  private saveSelectedAsOpeningDataset() {

    this.openingDataset =
      this.getDataset()
        .map(data => Object.assign({}, data));
  }

  private subscribeToCategoryChanges() {

    this.controls.category.valueChanges
      .pipe(
        untilDestroyed(this),
        takeWhile(_ => !!this.categoryFilter),
        debounceTime(DataSelectorComponent.DEBOUNCE_DELAY_MS),
        distinctUntilChanged(),
        tap(_ => this._loading = true),
        switchMap(category =>
          !!category
            ? this.categoryFilter.select(category)
            : this.categoryFilter.clear()
        ),
        tap(_ => this._loading = false),
      )
      .subscribe(() => this.refreshView());
  }

  private subscribeToSearchTermChanges() {

    this.controls.searchTerm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(DataSelectorComponent.DEBOUNCE_DELAY_MS),
        distinctUntilChanged(),
      )
      .subscribe(() => this.refreshView());
  }

}
